import { useRef, useEffect } from 'react';
import './PinCodeEntry.scss';
import { flattenPinCode, initializePinCode } from '../utils/commonUtils';

export interface PinCodeEntryProps {
  code: Map<number, number | null>;
  isInvalid: boolean;
  length?: number | undefined;
  setCode: (code: Map<number, number | null>) => void;
  setInputError: (error: string) => void;
}

const INVALID_KEY_ERROR = 'Only numbers 0-9 are allowed.';

export default function PinCodeEntry({
  code,
  isInvalid,
  length = 4,
  setCode,
  setInputError,
}: Readonly<PinCodeEntryProps>) {
  const inputRefs = useRef<HTMLInputElement[]>([]);

  /**
   * Hook to handle input focus after partial code input.
   * When the code is updated (code prop changes), focus
   * moves to the next available input.
   * If on the last input, it removes focus (blur).
   */
  useEffect(() => {
    let index = null;

    code.forEach((c, idx) => {
      if (Number.isInteger(c)) index = idx;
    });

    if (index !== null) {
      if (index < length - 1) {
        if (inputRefs?.current) inputRefs.current[index + 1].focus();
      } else {
        inputRefs.current[index].blur();
      }
    }
  }, [code, length]);

  const handleOnFocus = (index: number, value: string) => {
    let updatedCode = new Map(code);
    const valueInt = parseInt(value);

    if (isInvalid || flattenPinCode(code).length === length || index === 0) {
      updatedCode = initializePinCode(length);
    } else if (!Number.isInteger(valueInt)) {
      updatedCode.set(index, null);
    } else {
      updatedCode.set(index, valueInt);
    }

    setCode(updatedCode);
  };

  const getInputValue = (index: number) => {
    if (Number.isInteger(code.get(index))) return code.get(index)?.toString() || '';
    return '';
  };

  const handleKeyUp = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const updatedCode = new Map(code);
    const valueInt = parseInt(e.key);

    if (Number.isInteger(valueInt)) {
      updatedCode.set(index, valueInt);
      setCode(updatedCode);
    } else {
      setInputError(INVALID_KEY_ERROR);
    }
  };

  const handleInputWrapperTap = () => {
    inputRefs.current[0].focus();
  };

  const renderInputs = () => {
    const inputArr = [];

    for (let i = 0; i < length; i++) {
      inputArr.push(
        <input
          key={`input-${i}`}
          ref={(element) => {
            if (inputRefs?.current && element) inputRefs.current[i] = element;
          }}
          type="text"
          pattern="[0-9]*"
          className={`pin-code-entry__input${isInvalid ? ' pin-code-entry__input--invalid' : ''}`}
          maxLength={1}
          value={getInputValue(i)}
          onKeyUp={(e) => handleKeyUp(i, e)}
          onChange={() => {}}
          onFocus={() => handleOnFocus(i, '')}
        />,
      );
    }

    return inputArr;
  };

  return (
    <div className={`pin-code-entry${isInvalid ? ' pin-code-entry--invalid' : ''}`}>
      <input type="hidden" value={flattenPinCode(code)} />
      <button className="pin-code-entry__input-wrapper" onClick={handleInputWrapperTap}>
        {renderInputs()}
      </button>
    </div>
  );
}
